import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircleIcon,
  InfoIcon,
  WarningIcon,
  XIcon,
  type Icon,
} from '@phosphor-icons/react';

type ToastTone = 'error' | 'success' | 'info';

type Props = {
  message: ReactNode;
  tone?: ToastTone;
  // Auto-dismiss duration in ms. Pass 0 to disable auto-dismiss.
  duration?: number;
  onClose: () => void;
};

// Visual tone per toast type. Mirrors the PasswordGate card styling
// (translucent tinted background, matching colored border, soft glow) so
// the toast reads as part of the same brand language. Text + leading
// icon both pick up the tone color.
const TONE_CLASSES: Record<ToastTone, string> = {
  error: 'bg-red-soft border-red text-red glow-red',
  success: 'bg-green-soft border-green text-green glow-brand',
  info: 'bg-overlay border-border-strong text-fg',
};

const TONE_ICONS: Record<ToastTone, Icon> = {
  error: WarningIcon,
  success: CheckCircleIcon,
  info: InfoIcon,
};

// Portal-rendered toast pinned to the top-right of the viewport.
// Auto-dismisses after `duration` ms unless 0 is passed. The wrapping
// component owns the mount/unmount lifecycle — render `<Toast />` when
// there's a message, hide it by removing the element.
export function Toast({ message, tone = 'info', duration = 5000, onClose }: Props) {
  useEffect(() => {
    if (duration === 0) return;
    const t = setTimeout(onClose, duration);
    return () => clearTimeout(t);
  }, [duration, onClose]);

  // Leading status icon + close button both inherit the tone color via
  // the outer container's `text-{tone}` class — keeping them in sync
  // with the message text and the tone border/glow.
  const ToneIcon = TONE_ICONS[tone];

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="fixed top-6 right-6 z-50 max-w-sm w-[calc(100%-3rem)] sm:w-auto pointer-events-none"
    >
      <div
        className={
          'pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-2xl border ' +
          TONE_CLASSES[tone]
        }
      >
        <ToneIcon size={22} weight="regular" className="flex-none" />
        <div className="flex-1 text-sm leading-snug">{message}</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          // Negative margin paired with padding gives the button a bigger
          // hit target without changing the visual gap between it and the
          // message — the icon stays where it was, only the clickable
          // surface grows.
          className="flex-none p-2 -m-2 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
        >
          <XIcon size={18} weight="bold" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
